/**
 * Age Gate — Shopify
 * Versione: 2.0.0
 * Sessione: sessionStorage (una verifica per scheda)
 * Compatibile con: Dawn, Sense e tutti i temi Shopify standard
 * Applicazione: Globale su tutto il sito
 */

(function () {
  'use strict';

  // ─── Configurazione ──────────────────────────────────────────────
  const CONFIG = {
    storageKey: 'age_verified',        // chiave sessionStorage
    storageValue: 'true',              // valore da salvare se verificato
    overlayId: 'age-gate-overlay',     // ID del popup nel DOM
    animationDuration: 300,            // ms per la transizione
    cartApiUrl: '/cart.js',            // API carrello Shopify
    cartUpdateUrl: '/cart/update.js',  // API aggiornamento carrello
    ageRestrictedTag: 'age-restricted' // tag identificativo prodotti
  };

  // ─── Verifica se l'utente è già stato verificato ─────────────────
  function isVerified() {
    try {
      return sessionStorage.getItem(CONFIG.storageKey) === CONFIG.storageValue;
    } catch (e) {
      // sessionStorage non disponibile (es. modalità privata con restrizioni)
      return false;
    }
  }

  // ─── Salva la verifica nella sessione corrente ───────────────────
  function setVerified() {
    try {
      sessionStorage.setItem(CONFIG.storageKey, CONFIG.storageValue);
    } catch (e) {
      console.warn('[AgeGate] Impossibile accedere a sessionStorage:', e);
    }
  }

  // ─── Verifica se la pagina è nella lista di esclusioni ─────────────
  function isExcludedPage(excludedPages) {
    if (!excludedPages || excludedPages.length === 0) return false;

    const currentPath = window.location.pathname;

    for (const page of excludedPages) {
      const trimmedPage = page.trim();
      if (!trimmedPage) continue; // salta le righe vuote

      // Confronto esatto (es. /pages/contatti === /pages/contatti)
      if (currentPath === trimmedPage) {
        return true;
      }

      // Confronto come cartella padre (es. /policies/* quando trimmedPage è /policies)
      // Verifica che dopo il percorso ci sia uno slash per evitare falsi positivi
      // Es: /policies/terms-of-service inizia con /policies/ → esclude
      // Es: /policies-faq inizia con /policies ma NON con /policies/ → non esclude
      if (currentPath.startsWith(trimmedPage + '/')) {
        return true;
      }
    }

    return false;
  }

  // ─── Mostra il popup ─────────────────────────────────────────────
  function showOverlay() {
    const overlay = document.getElementById(CONFIG.overlayId);
    if (!overlay) return;

    // Rimuovi hidden e aggiungi classe per animazione
    overlay.removeAttribute('hidden');
    document.body.style.overflow = 'hidden'; // blocca scroll
    document.body.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('aria-hidden', 'false');

    // Focus trap: porta il focus al primo elemento interattivo
    requestAnimationFrame(() => {
      const firstBtn = overlay.querySelector('button');
      if (firstBtn) firstBtn.focus();
    });
  }

  // ─── Nascondi il popup con animazione ────────────────────────────
  function hideOverlay() {
    const overlay = document.getElementById(CONFIG.overlayId);
    if (!overlay) return;

    overlay.classList.add('age-gate-overlay--hiding');
    document.body.removeAttribute('style');
    document.body.removeAttribute('aria-hidden');

    setTimeout(() => {
      overlay.setAttribute('hidden', '');
      overlay.classList.remove('age-gate-overlay--hiding');
    }, CONFIG.animationDuration);
  }

  // ─── Gestione click "Sì" ────────────────────────────────────────
  function handleYes() {
    setVerified();
    hideOverlay();
  }

  // ─── Gestione click "No" ────────────────────────────────────────
  async function handleNo() {
    const overlay = document.getElementById(CONFIG.overlayId);
    const redirectUrl = overlay
      ? overlay.dataset.redirectUrl
      : '/pages/accesso-negato';

    // Se siamo nel checkout o nel carrello, rimuovi i prodotti riservati
    const isCartOrCheckout =
      window.location.pathname.includes('/checkout') ||
      window.location.pathname.includes('/cart');

    if (isCartOrCheckout) {
      await removeRestrictedProductsFromCart();
    }

    window.location.href = redirectUrl;
  }

  // ─── Rimuovi prodotti age-restricted dal carrello ────────────────
  async function removeRestrictedProductsFromCart() {
    try {
      // Ottieni il carrello corrente
      const response = await fetch(CONFIG.cartApiUrl);
      if (!response.ok) return;

      const cart = await response.json();
      const updates = {};

      // Per ogni prodotto nel carrello, verifica se è age-restricted
      // tramite le proprietà custom (impostate al momento dell'aggiunta)
      for (const item of cart.items) {
        if (item.properties && item.properties['_age_restricted'] === 'true') {
          updates[item.key] = 0; // imposta quantità a 0 = rimozione
        }
      }

      if (Object.keys(updates).length > 0) {
        await fetch(CONFIG.cartUpdateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });
      }
    } catch (err) {
      console.warn('[AgeGate] Errore rimozione prodotti:', err);
    }
  }

  // ─── Focus trap (accessibilità) ──────────────────────────────────
  function trapFocus(event) {
    const overlay = document.getElementById(CONFIG.overlayId);
    if (!overlay || overlay.hasAttribute('hidden')) return;

    const focusable = overlay.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusable[0];
    const lastFocusable = focusable[focusable.length - 1];

    if (event.key !== 'Tab') return;

    if (event.shiftKey) {
      if (document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    }
  }

  // ─── Inizializzazione ────────────────────────────────────────────
  function init() {
    const overlay = document.getElementById(CONFIG.overlayId);
    if (!overlay) return; // snippet non presente in questa pagina

    // Leggi la lista delle pagine escluse dal data-attribute
    let excludedPages = [];
    try {
      const excludedPagesStr = overlay.dataset.excludedPages || '';
      if (excludedPagesStr) {
        excludedPages = excludedPagesStr
          .split('\n')
          .map(p => p.trim())
          .filter(p => p);
      }
    } catch (e) {
      console.warn('[AgeGate] Errore parsing pagine escluse:', e);
    }

    // Non mostrare il popup sulle pagine escluse
    if (isExcludedPage(excludedPages)) return;

    // Se già verificato in questa sessione, non mostrare nulla
    if (isVerified()) return;

    // Mostra il popup
    showOverlay();

    // Event listeners pulsanti
    const btnYes = document.getElementById('age-gate-yes');
    const btnNo = document.getElementById('age-gate-no');

    if (btnYes) btnYes.addEventListener('click', handleYes);
    if (btnNo) btnNo.addEventListener('click', handleNo);

    // Focus trap per accessibilità
    document.addEventListener('keydown', trapFocus);
  }

  // ─── Avvio al caricamento DOM ────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();